<script setup lang="ts">
import {computed} from "vue";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import ChatMediaMenu from "@/components/navBar/chat/ChatMediaMenu.vue";
import ChatMessageTicks from "@/components/navBar/chat/ChatMessageTicks.vue";
import {MessageMetadata} from "@/utils/presenters/interfaces.ts";

const props = defineProps<{
  messages: MessageMetadata[];
  sent: boolean;
  stamp: string;
  status?: string;
  darkMode: boolean;
}>();

const emit = defineEmits<{
  previewAlbum: [messages: MessageMetadata[], startIndex: number];
}>();

const MAX_VISIBLE = 4;
const visibleMessages = computed(() => props.messages.slice(0, MAX_VISIBLE));
const extraCount = computed(() => Math.max(0, props.messages.length - MAX_VISIBLE));

const gridClass = computed(() => {
  const count = visibleMessages.value.length;
  if (count === 2) return 'tw-grid-cols-2';
  return 'tw-grid-cols-2';
});

function getSrc(msg: MessageMetadata): string {
  return ChatUtils.getFileDownloadUrl(msg.attachment?.attachmentInfo?.fileId);
}

function getDownloadUrl(msg: MessageMetadata): string {
  return ChatUtils.getFileDownloadUrl(msg.attachment?.attachmentInfo?.fileId);
}

function isVideo(msg: MessageMetadata): boolean {
  return ChatUtils.isVideoMessage(msg);
}

function onTileClick(index: number) {
  emit('previewAlbum', props.messages, index);
}

function shouldSpanFull(index: number): boolean {
  return visibleMessages.value.length === 3 && index === 0;
}
</script>

<template>
  <div class="chat-media-album tw-max-w-[280px] tw-rounded-lg tw-overflow-hidden"
       :class="[sent ? 'tw-self-end' : 'tw-self-start']">
    <div class="tw-grid tw-gap-[2px]" :class="[gridClass]">
      <div v-for="(msg, index) in visibleMessages" :key="msg.id || index"
           class="tw-relative tw-cursor-pointer tw-group tw-overflow-hidden"
           :class="[shouldSpanFull(index) ? 'tw-col-span-2' : '']"
           @click="onTileClick(index)">
        <template v-if="isVideo(msg)">
          <video :src="getSrc(msg)" preload="metadata"
                 class="tw-w-full tw-h-[140px] tw-object-cover tw-block"
                 :class="[shouldSpanFull(index) ? 'tw-h-[180px]' : '']"/>
          <div class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none">
            <div class="tw-bg-black/50 tw-rounded-full tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center">
              <q-icon name="play_arrow" size="1.2rem" color="white"/>
            </div>
          </div>
        </template>
        <template v-else>
          <img :src="getSrc(msg)" :alt="msg.attachment?.attachmentInfo?.fileName"
               class="tw-w-full tw-h-[140px] tw-object-cover tw-block"
               :class="[shouldSpanFull(index) ? 'tw-h-[180px]' : '']"
               loading="lazy"/>
        </template>
        <div class="tw-absolute tw-top-1 tw-right-1 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" @click.stop>
          <ChatMediaMenu :download-url="getDownloadUrl(msg)" btn-size="xs" btn-class="tw-bg-black/40 tw-text-white"/>
        </div>
        <div v-if="index === MAX_VISIBLE - 1 && extraCount > 0"
             class="tw-absolute tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center">
          <span class="tw-text-white tw-text-2xl tw-font-semibold">+{{ extraCount }}</span>
        </div>
      </div>
    </div>
    <div class="tw-px-2 tw-py-1 tw-text-[10px] tw-flex tw-items-center tw-justify-end tw-gap-1"
         :class="[sent ? 'tw-bg-[var(--q-primary)] tw-text-white' : (darkMode ? 'tw-bg-gray-800 tw-text-white' : 'tw-bg-gray-200 tw-text-gray-500')]">
      <span v-text="stamp"/>
      <ChatMessageTicks v-if="sent" :status="status"/>
    </div>
  </div>
</template>
